import { deleteCourseContentCallable } from '@/lib/firebase';
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { IQuizRepository } from './interfaces';
import { CourseRepository } from './course.repository';

// Quiz entity interfaces
export interface CreateQuizData {
  title: string;
  description?: string;
  courseId?: string;
  lessonId?: string;
  timeLimit?: number;
  passingScore?: number;
  maxAttempts?: number;
  status?: 'draft' | 'published' | 'archived';
  questionCount?: number;
  isActive?: boolean;
  allowRetakeAfterPass?: boolean;
  showResultsImmediately?: boolean;
  shuffleQuestions?: boolean;
}

export interface UpdateQuizData {
  title?: string;
  description?: string;
  courseId?: string;
  lessonId?: string;
  timeLimit?: number;
  passingScore?: number;
  maxAttempts?: number;
  status?: 'draft' | 'published' | 'archived';
  questionCount?: number;
  isActive?: boolean;
  allowRetakeAfterPass?: boolean;
  showResultsImmediately?: boolean;
  shuffleQuestions?: boolean;
  updatedAt?: Timestamp;
}

export interface QuizFilter {
  courseId?: string;
  lessonId?: string;
  status?: 'draft' | 'published' | 'archived';
  isActive?: boolean;
  instructorId?: string;
}

export interface GetQuizData {
  id: string;
  title: string;
  description?: string;
  courseId?: string;
  lessonId?: string;
  timeLimit?: number;
  passingScore?: number;
  maxAttempts?: number;
  status: 'draft' | 'published' | 'archived';
  questionCount: number;
  isActive: boolean;
  allowRetakeAfterPass: boolean;
  showResultsImmediately: boolean;
  shuffleQuestions: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface QuizQuestionData {
  id: string;
  quizId: string;
  question: string;
  type: 'multipleChoice' | 'trueFalse' | 'shortAnswer' | 'essay' | 'matching';
  options?: string[];
  correctAnswer: string | string[] | { [key: string]: string };
  points: number;
  explanation?: string;
  order: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface QuizAttemptData {
  id: string;
  userId: string;
  quizId: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeTaken?: number;
  completedAt: Timestamp;
  status: 'inProgress' | 'completed' | 'timedOut';
}

/** Questions sent inline with a quiz (callers rarely pass questionCount). */
function countQuestions(data: unknown): number {
  const q = (data as { questions?: unknown } | null)?.questions;
  return Array.isArray(q) ? q.length : 0;
}

export class QuizRepository implements IQuizRepository {
  private collectionName = 'quizzes';
  private questionsCollectionName = 'quizQuestions';
  private attemptsCollectionName = 'quizAttempts';
  private optionsCollectionName = 'quizOptions';
  private attemptAnswersCollectionName = 'quizAttemptAnswers';

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(_db?: unknown) {
    // Legacy param kept for DI compatibility — all queries use the client SDK db from @/lib/firebase.
  }

  private toError(error: unknown, code: string, devMessage: string, context?: Record<string, unknown>) {
    return toDRXError(error, { code, devMessage, context });
  }

  // --------------- Quiz CRUD ---------------

  async createQuiz(quizData: CreateQuizData): Promise<GetQuizData> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...quizData,
        status: quizData.status || 'draft',
        // Count what was actually sent (callers rarely pass questionCount).
        questionCount: quizData.questionCount || countQuestions(quizData),
        isActive: quizData.isActive ?? true,
        allowRetakeAfterPass: quizData.allowRetakeAfterPass ?? false,
        showResultsImmediately: quizData.showResultsImmediately ?? true,
        shuffleQuestions: quizData.shuffleQuestions ?? false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({ code: 'QUIZ_CREATE_MISSING_DOCUMENT', devMessage: 'Quiz missing after create' });
      }
      return { id: docRef.id, ...docSnap.data() } as GetQuizData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw this.toError(error, 'QUIZ_CREATE_FAILED', 'Failed to create quiz', { title: quizData.title });
    }
  }

  async getQuizById(quizId: string): Promise<GetQuizData | null> {
    try {
      const docSnap = await getDoc(doc(db, this.collectionName, quizId));
      if (!docSnap.exists()) return null;
      return { id: docSnap.id, ...docSnap.data() } as GetQuizData;
    } catch (error) {
      throw this.toError(error, 'QUIZ_FETCH_FAILED', `Failed to get quiz ${quizId}`, { quizId });
    }
  }

  async getQuizzes(filter: QuizFilter = {}, limitCount?: number): Promise<GetQuizData[]> {
    try {
      const constraints: QueryConstraint[] = [];

      if (filter.courseId) constraints.push(where('courseId', '==', filter.courseId));
      if (filter.lessonId) constraints.push(where('lessonId', '==', filter.lessonId));
      if (filter.status) constraints.push(where('status', '==', filter.status));
      if (filter.isActive !== undefined) constraints.push(where('isActive', '==', filter.isActive));
      constraints.push(orderBy('createdAt', 'desc'));
      if (limitCount) constraints.push(limit(limitCount));

      const snapshot = await getDocs(query(collection(db, this.collectionName), ...constraints));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GetQuizData));
    } catch (error) {
      throw this.toError(error, 'QUIZZES_FETCH_FAILED', 'Failed to get quizzes', { filter, limitCount });
    }
  }

  async updateQuiz(quizId: string, updateData: UpdateQuizData): Promise<GetQuizData> {
    try {
      const docRef = doc(db, this.collectionName, quizId);
      await updateDoc(docRef, { ...updateData, updatedAt: serverTimestamp() });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({ code: 'QUIZ_NOT_FOUND_AFTER_UPDATE', devMessage: `Quiz ${quizId} missing after update`, level: 'warning' });
      }
      return { id: docSnap.id, ...docSnap.data() } as GetQuizData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw this.toError(error, 'QUIZ_UPDATE_FAILED', `Failed to update quiz ${quizId}`, { quizId });
    }
  }

  async deleteQuiz(quizId: string): Promise<void> {
    try {
      // On the server: it removes the questions, options and attempts and
      // checks the caller owns the course. Attempts are server-only data, so
      // deleting them from the browser was refused and no quiz could be
      // deleted (2026-10-14).
      await deleteCourseContentCallable({ scope: 'quiz', id: quizId });
    } catch (error) {
      throw this.toError(error, 'QUIZ_DELETE_FAILED', `Failed to delete quiz ${quizId}`, { quizId });
    }
  }

  // --------------- Quiz lookups ---------------

  async getQuizByTitle(title: string): Promise<GetQuizData | null> {
    try {
      const snapshot = await getDocs(query(collection(db, this.collectionName), where('title', '==', title)));
      if (snapshot.empty) return null;
      const d = snapshot.docs[0];
      return { id: d.id, ...d.data() } as GetQuizData;
    } catch (error) {
      throw this.toError(error, 'QUIZ_BY_TITLE_FETCH_FAILED', `Failed to get quiz by title ${title}`, { title });
    }
  }

  async getQuizzesByCourse(courseId: string): Promise<GetQuizData[]> {
    return this.getQuizzes({ courseId });
  }

  async getQuizzesByLesson(lessonId: string): Promise<GetQuizData[]> {
    return this.getQuizzes({ lessonId });
  }

  async getQuizzesByModule(moduleId: string): Promise<GetQuizData[]> {
    try {
      const snapshot = await getDocs(
        query(collection(db, this.collectionName), where('moduleId', '==', moduleId), orderBy('orderIndex', 'asc')),
      );
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GetQuizData));
    } catch (error) {
      throw this.toError(error, 'QUIZZES_BY_MODULE_FETCH_FAILED', `Failed to get quizzes for module ${moduleId}`, { moduleId });
    }
  }

  async getQuizzesByInstructor(instructorId: string): Promise<GetQuizData[]> {
    try {
      const courseRepo = new CourseRepository();
      const courses = await courseRepo.getCoursesByInstructor(instructorId);
      const courseIds = courses.map(c => c.id);
      if (courseIds.length === 0) return [];

      const quizzes: GetQuizData[] = [];
      for (const courseId of courseIds) {
        const courseQuizzes = await this.getQuizzesByCourse(courseId);
        quizzes.push(...courseQuizzes);
      }
      return quizzes;
    } catch (error) {
      throw this.toError(error, 'QUIZZES_BY_INSTRUCTOR_FETCH_FAILED', `Failed to get quizzes for instructor ${instructorId}`, { instructorId });
    }
  }

  async getQuizzesByOwner(ownerId: string, _limitCount?: number): Promise<GetQuizData[]> {
    return this.getQuizzesByInstructor(ownerId);
  }

  async getQuizzesForUser(userId: string, isAdmin = false, limitCount?: number): Promise<GetQuizData[]> {
    return isAdmin ? this.getQuizzes({}, limitCount) : this.getQuizzesByOwner(userId, limitCount);
  }

  async getAllQuizzes(limitCount?: number): Promise<GetQuizData[]> {
    return this.getQuizzes({}, limitCount);
  }

  async getPublishedQuizzes(limitCount?: number): Promise<GetQuizData[]> {
    return this.getQuizzes({ status: 'published', isActive: true }, limitCount);
  }

  // --------------- Quiz questions ---------------

  async addQuizQuestion(questionData: Omit<QuizQuestionData, 'id' | 'createdAt' | 'updatedAt'>): Promise<QuizQuestionData> {
    try {
      const docRef = await addDoc(collection(db, this.questionsCollectionName), {
        ...questionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({ code: 'QUIZ_QUESTION_CREATE_MISSING_DOCUMENT', devMessage: 'Question missing after create' });
      }
      return { id: docRef.id, ...docSnap.data() } as QuizQuestionData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw this.toError(error, 'QUIZ_QUESTION_CREATE_FAILED', `Failed to add question to quiz ${questionData.quizId}`);
    }
  }

  async getQuizQuestions(quizId: string): Promise<QuizQuestionData[]> {
    try {
      // Sorted here, not with orderBy('order'): the course editor saves the
      // position as orderIndex, and Firestore drops every document missing
      // the orderBy field -- so its quizzes showed "No questions added yet"
      // although the questions were saved (2026-10-14).
      const snapshot = await getDocs(
        query(collection(db, this.questionsCollectionName), where('quizId', '==', quizId)),
      );
      const position = (q: Record<string, unknown>) => Number(q.order ?? q.orderIndex ?? 0);
      if (snapshot.empty) {
        // Quizzes made in the Exams builder keep their questions on the quiz
        // itself (the server reads either); the course editor only looked at
        // quizQuestions, so those quizzes showed "No questions added yet".
        const quiz = await getDoc(doc(db, this.collectionName, quizId));
        const embedded = quiz.exists() ? (quiz.data().questions as Record<string, unknown>[] | undefined) : undefined;
        return (Array.isArray(embedded) ? embedded : [])
          .map((q, i) => ({ id: String(q.id ?? `q${i}`), quizId, ...q } as unknown as QuizQuestionData))
          .sort((a, b) => position(a as unknown as Record<string, unknown>) - position(b as unknown as Record<string, unknown>));
      }
      return snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as QuizQuestionData))
        .sort((a, b) => position(a as unknown as Record<string, unknown>) - position(b as unknown as Record<string, unknown>));
    } catch (error) {
      throw this.toError(error, 'QUIZ_QUESTIONS_FETCH_FAILED', `Failed to get questions for quiz ${quizId}`, { quizId });
    }
  }

  async updateQuizQuestion(questionId: string, updateData: Partial<QuizQuestionData>): Promise<QuizQuestionData> {
    try {
      const docRef = doc(db, this.questionsCollectionName, questionId);
      await updateDoc(docRef, { ...updateData, updatedAt: serverTimestamp() });
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({ code: 'QUIZ_QUESTION_NOT_FOUND_AFTER_UPDATE', devMessage: `Question ${questionId} missing after update`, level: 'warning' });
      }
      return { id: docSnap.id, ...docSnap.data() } as QuizQuestionData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw this.toError(error, 'QUIZ_QUESTION_UPDATE_FAILED', `Failed to update quiz question ${questionId}`);
    }
  }

  async deleteQuizQuestion(questionId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.questionsCollectionName, questionId));
    } catch (error) {
      throw this.toError(error, 'QUIZ_QUESTION_DELETE_FAILED', `Failed to delete quiz question ${questionId}`);
    }
  }

  async deleteQuizQuestions(quizId: string): Promise<void> {
    try {
      const questions = await this.getQuizQuestions(quizId);
      await Promise.all(questions.map(q => deleteDoc(doc(db, this.questionsCollectionName, q.id))));
    } catch (error) {
      throw this.toError(error, 'QUIZ_QUESTIONS_DELETE_FAILED', `Failed to delete questions for quiz ${quizId}`, { quizId });
    }
  }

  // --------------- Quiz attempts ---------------

  async submitQuizAttempt(attemptData: Omit<QuizAttemptData, 'id' | 'completedAt'>): Promise<QuizAttemptData> {
    try {
      const docRef = await addDoc(collection(db, this.attemptsCollectionName), {
        ...attemptData,
        completedAt: serverTimestamp(),
      });
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({ code: 'QUIZ_ATTEMPT_CREATE_MISSING_DOCUMENT', devMessage: 'Attempt missing after create' });
      }
      return { id: docRef.id, ...docSnap.data() } as QuizAttemptData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw this.toError(error, 'QUIZ_ATTEMPT_CREATE_FAILED', `Failed to submit attempt for quiz ${attemptData.quizId}`);
    }
  }

  async getQuizAttemptsByUser(userId: string, quizId?: string): Promise<QuizAttemptData[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('userId', '==', userId),
        orderBy('completedAt', 'desc'),
      ];
      if (quizId) constraints.push(where('quizId', '==', quizId));

      const snapshot = await getDocs(query(collection(db, this.attemptsCollectionName), ...constraints));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as QuizAttemptData));
    } catch (error) {
      throw this.toError(error, 'QUIZ_ATTEMPTS_FETCH_FAILED', `Failed to get quiz attempts for user ${userId}`, { userId, quizId });
    }
  }

  async deleteQuizAttempts(quizId: string): Promise<void> {
    try {
      const snapshot = await getDocs(
        query(collection(db, this.attemptsCollectionName), where('quizId', '==', quizId)),
      );
      await Promise.all(snapshot.docs.map(d => deleteDoc(d.ref)));
    } catch (error) {
      throw this.toError(error, 'QUIZ_ATTEMPTS_DELETE_FAILED', `Failed to delete quiz attempts for quiz ${quizId}`, { quizId });
    }
  }

  // --------------- Quiz options & attempt answers ---------------

  async duplicateQuiz(quizId: string, currentUserId?: string): Promise<Record<string, unknown> & { id: string }> {
    try {
      const originalDoc = await getDoc(doc(db, this.collectionName, quizId));
      if (!originalDoc.exists()) {
        throw new DRXError({ code: 'QUIZ_NOT_FOUND', devMessage: `Quiz ${quizId} not found for duplication`, level: 'warning' });
      }

      const quizData = originalDoc.data();
      const instructorId = currentUserId || quizData?.instructorId || quizData?.userId || quizData?.createdBy;
      if (!instructorId) {
        throw new DRXError({ code: 'QUIZ_DUPLICATE_NO_INSTRUCTOR', devMessage: 'No instructor for quiz duplication' });
      }

      const duplicateData = {
        ...quizData,
        title: `${quizData.title} (Copy)`,
        status: 'draft',
        instructorId,
        userId: instructorId,
        createdBy: instructorId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        questionCount: quizData.questionCount || 0,
        passingScore: quizData.passingScore || 70,
        timeLimit: quizData.timeLimit,
        isActive: quizData.isActive !== false,
        allowRetake: quizData.allowRetake !== false,
        shuffleQuestions: quizData.shuffleQuestions !== false,
        showResultsImmediately: quizData.showResultsImmediately !== false,
      };

      const newQuiz = await addDoc(collection(db, this.collectionName), duplicateData);
      return { id: newQuiz.id, ...duplicateData };
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({ code: 'QUIZ_DUPLICATE_FAILED', devMessage: `Failed to duplicate quiz ${quizId}`, cause: error });
    }
  }

  async createQuizOption(optionData: Record<string, unknown>): Promise<{ id: string }> {
    try {
      const docRef = await addDoc(collection(db, this.optionsCollectionName), { ...optionData, createdAt: serverTimestamp() });
      return { id: docRef.id };
    } catch (error) {
      throw new DRXError({ code: 'QUIZ_OPTION_CREATE_FAILED', devMessage: 'Failed to create quiz option', cause: error });
    }
  }

  async getQuizOptions(questionId: string): Promise<Array<Record<string, unknown> & { id: string }>> {
    try {
      const snapshot = await getDocs(query(collection(db, this.optionsCollectionName), where('questionId', '==', questionId)));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({ code: 'QUIZ_OPTIONS_FETCH_FAILED', devMessage: `Failed to fetch options for question ${questionId}`, cause: error });
    }
  }

  async createQuizAttemptAnswer(answerData: Record<string, unknown>): Promise<{ id: string }> {
    try {
      const docRef = await addDoc(collection(db, this.attemptAnswersCollectionName), { ...answerData, createdAt: serverTimestamp() });
      return { id: docRef.id };
    } catch (error) {
      throw new DRXError({ code: 'QUIZ_ATTEMPT_ANSWER_CREATE_FAILED', devMessage: 'Failed to create quiz attempt answer', cause: error });
    }
  }

  async getQuizAttemptAnswers(attemptId: string): Promise<Array<Record<string, unknown> & { id: string }>> {
    try {
      const snapshot = await getDocs(query(collection(db, this.attemptAnswersCollectionName), where('attemptId', '==', attemptId)));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({ code: 'QUIZ_ATTEMPT_ANSWERS_FETCH_FAILED', devMessage: `Failed to fetch answers for attempt ${attemptId}`, cause: error });
    }
  }

  async createQuizAttempt(attemptData: Record<string, unknown>): Promise<Record<string, unknown> & { id: string }> {
    try {
      const docRef = await addDoc(collection(db, this.attemptsCollectionName), { ...attemptData, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      return { id: docRef.id, ...attemptData };
    } catch (error) {
      throw new DRXError({ code: 'QUIZ_ATTEMPT_CREATE_FAILED', devMessage: 'Failed to create quiz attempt', cause: error });
    }
  }

  async updateQuizAttempt(attemptId: string, attemptData: Record<string, unknown>): Promise<Record<string, unknown> & { id: string }> {
    try {
      await updateDoc(doc(db, this.attemptsCollectionName, attemptId), { ...attemptData, updatedAt: serverTimestamp() });
      return { id: attemptId, ...attemptData };
    } catch (error) {
      throw new DRXError({ code: 'QUIZ_ATTEMPT_UPDATE_FAILED', devMessage: `Failed to update quiz attempt ${attemptId}`, cause: error });
    }
  }

  async uploadQuizQuestionImage(_quizId: string, _questionId: string, file: File): Promise<string> {
    // Placeholder — real implementation would use Firebase Storage
    await new Promise(resolve => setTimeout(resolve, 500));
    return `https://storage.example.com/quizzes/${_quizId}/questions/${_questionId}/image_${Date.now()}_${file.name}`;
  }
}
